import { GlobalStyles } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { replacesLastRecorded } from '../../utils/scraperScenarios';

// The code editor's side of Debug and Record, driven by ScenarioPreviewPanel:
// breakpoints in the glyph margin, the line a run is paused on, and recorded lines
// put into the code.
//
// A run's line numbers are those of the code as it was sent. Recorded lines go into
// the editor while it runs, which moves everything below them, so each line of that
// snapshot is tracked by a decoration of its own: Monaco moves decorations with the
// text, and "line N of the run" is wherever the Nth one is now.

const RECORDER_SOURCE = 'scraper-recorder';

const CLASSES = {
    breakpoint: 'scraper-breakpoint',
    paused: 'scraper-paused-line',
    pausedGlyph: 'scraper-paused-glyph',
    failed: 'scraper-failed-line',
    failedGlyph: 'scraper-failed-glyph',
    recorded: 'scraper-recorded-line'
};

/** The editor options the debugger needs: a glyph margin to click for breakpoints. */
export const DEBUGGER_EDITOR_OPTIONS = { glyphMargin: true };

/** The decorations' looks; rendered once by the page. */
export const ScenarioDebuggerStyles = () => (
    <GlobalStyles
        styles={({ palette }) => ({
            [`.${CLASSES.breakpoint}`]: {
                background: `radial-gradient(circle at center, ${palette.error.main} 0, ${palette.error.main} 4px, transparent 5px)`,
                cursor: 'pointer'
            },
            [`.${CLASSES.paused}`]: { background: alpha(palette.warning.main, 0.28) },
            [`.${CLASSES.failed}`]: { background: alpha(palette.error.main, 0.22) },
            [`.${CLASSES.recorded}`]: { background: alpha(palette.success.main, 0.18) },
            [`.${CLASSES.pausedGlyph}::after, .${CLASSES.failedGlyph}::after`]: {
                content: '"▶"',
                display: 'block',
                textAlign: 'center',
                fontSize: 11
            },
            [`.${CLASSES.pausedGlyph}::after`]: { color: palette.warning.main },
            [`.${CLASSES.failedGlyph}::after`]: { color: palette.error.main }
        })}
    />
);

const leadingSpace = (text) => /^\s*/.exec(text)[0];

/**
 * Wraps one Monaco editor (from CodeEditor's onMount). Breakpoints are toggled by a
 * click in the glyph margin, before, during and after a run.
 */
export function createScenarioDebugger(editor, monaco) {
    const { Range } = monaco;
    const stickiness = monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges;
    const breakpoints = editor.createDecorationsCollection();
    const current = editor.createDecorationsCollection();
    const listeners = new Set();
    let snapshot = null;
    let last = null;

    const model = () => editor.getModel();
    const lineMarker = (line, options = {}) => ({ range: new Range(line, 1, line, 1), options: { stickiness, ...options } });

    const breakpointLines = () =>
        [...new Set(breakpoints.getRanges().map((range) => range.startLineNumber))].sort((a, b) => a - b);

    /** The line of the run's code now at editor line `line`; null for a line added since. */
    const toRunLine = (line) => {
        if (!snapshot) return line;
        for (let index = 0; index < snapshot.length; index += 1) {
            if (snapshot.getRange(index)?.startLineNumber === line) return index + 1;
        }
        return null;
    };

    /** Where line `line` of the run's code is in the editor now. */
    const toEditorLine = (line) => {
        if (!snapshot) return line;
        return snapshot.getRange(line - 1)?.startLineNumber ?? null;
    };

    const runBreakpoints = () => breakpointLines().map(toRunLine).filter(Boolean);

    const setBreakpoints = (lines) => {
        breakpoints.set(lines.map((line) => lineMarker(line, { glyphMarginClassName: CLASSES.breakpoint })));
        const runLines = runBreakpoints();
        listeners.forEach((listener) => listener(runLines));
    };

    const mouse = editor.onMouseDown((event) => {
        const { target } = event;
        if (target.type !== monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN || !target.position) return;
        const line = target.position.lineNumber;
        const lines = breakpointLines();
        setBreakpoints(lines.includes(line) ? lines.filter((item) => item !== line) : [...lines, line]);
    });

    const placeCursor = (line) => {
        const text = model();
        editor.setPosition({ lineNumber: line, column: text.getLineMaxColumn(line) });
    };

    // Where recorded lines should go when the operator has not put the cursor
    // anywhere: just above a paused action, or above the script's closing return.
    const placeCursorFor = (editorLine, reason) => {
        if (editor.hasTextFocus()) return;
        const text = model();
        if (editorLine) {
            placeCursor(Math.max(1, editorLine - 1));
            return;
        }
        if (reason !== 'end') return;
        let lastLine = text.getLineCount();
        while (lastLine > 1 && text.getLineContent(lastLine).trim() === '') lastLine -= 1;
        placeCursor(/^\s*return\b/.test(text.getLineContent(lastLine)) && lastLine > 1 ? lastLine - 1 : lastLine);
    };

    return {
        /**
         * A run is starting with the code as it is now: its lines are tracked from
         * here on. Returns the breakpoints, as lines of that code.
         */
        startRun() {
            snapshot?.clear();
            const count = model().getLineCount();
            snapshot = editor.createDecorationsCollection(Array.from({ length: count }, (_, index) => lineMarker(index + 1)));
            last?.collection.clear();
            last = null;
            current.clear();
            return breakpointLines();
        },

        endRun() {
            current.clear();
            snapshot?.clear();
            snapshot = null;
        },

        /** `listener(lines)` whenever the breakpoints change, as lines of the running code. Returns the unsubscribe. */
        onBreakpointsChange(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },

        /** Marks the line a run is paused on — `failed` when it failed there — or clears the mark (`line` null). */
        showPaused(line, { failed = false, reason } = {}) {
            const at = line ? toEditorLine(line) : null;
            if (!at) {
                current.clear();
                placeCursorFor(null, reason);
                return;
            }
            current.set([
                lineMarker(at, {
                    isWholeLine: true,
                    className: failed ? CLASSES.failed : CLASSES.paused,
                    glyphMarginClassName: failed ? CLASSES.failedGlyph : CLASSES.pausedGlyph
                })
            ]);
            editor.revealLineInCenterIfOutsideViewport(at);
            placeCursorFor(at, reason);
        },

        clearPaused() {
            current.clear();
        },

        /**
         * Puts a recorded line below the cursor, at its line's indent — or, for a
         * new value of the field typed into last, in place of that line. One undo
         * step each. False when the editor is read-only.
         */
        insertRecorded(recorded) {
            const text = model();
            if (!text || editor.getOption(monaco.editor.EditorOption.readOnly)) return false;

            const previous = last && replacesLastRecorded(last.recorded, recorded) ? last.collection.getRange(0) : null;
            let line;
            if (previous) {
                line = previous.startLineNumber;
                const indent = leadingSpace(text.getLineContent(line));
                editor.executeEdits(RECORDER_SOURCE, [
                    { range: new Range(line, 1, line, text.getLineMaxColumn(line)), text: indent + recorded.code }
                ]);
            } else {
                const at = editor.getPosition()?.lineNumber ?? text.getLineCount();
                const content = text.getLineContent(at);
                const end = text.getLineMaxColumn(at);
                if (content.trim() === '') {
                    editor.executeEdits(RECORDER_SOURCE, [{ range: new Range(at, 1, at, end), text: content + recorded.code }]);
                    line = at;
                } else {
                    editor.executeEdits(RECORDER_SOURCE, [
                        { range: new Range(at, end, at, end), text: `\n${leadingSpace(content)}${recorded.code}` }
                    ]);
                    line = at + 1;
                }
            }
            editor.pushUndoStop();
            placeCursor(line);
            editor.revealLineInCenterIfOutsideViewport(line);

            last?.collection.clear();
            last = {
                recorded,
                collection: editor.createDecorationsCollection([
                    lineMarker(line, { isWholeLine: true, className: CLASSES.recorded })
                ])
            };
            return true;
        },

        dispose() {
            mouse.dispose();
            listeners.clear();
            breakpoints.clear();
            current.clear();
            snapshot?.clear();
            last?.collection.clear();
        }
    };
}
